import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { setHtmlElementOptions } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { anyReallyAny } from "@cervval.js/common/src/tools/any.tools";
import { wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { DragAndDropAbortEvent, DragAndDropEvent } from "./dragAndDrop.event";
import { CJS_DRAGGABLE_ITEM_ATTR, CJS_DROP_CONTAINER_ATTR, DragRefConfig } from "./dragAndDrop.types";
import { DragRef } from "./dragRef";


export const DRAG_AND_DROP_SERVICE = "DragAndDropService";

/** 
 * Service that allows for drag-and-drop functionality to be attached to DOM elements.
 * Inspired from Angular DragService https://material.angular.io/cdk/drag-drop/api#DragDrop
 */
@Injectable(DRAG_AND_DROP_SERVICE)
export class DragAndDropService {

    /**
     * The dragstart event is fired when the user starts dragging an element or text selection.
     */
    @Event()
    public dragStartEvent!: EventHandler<DragAndDropEvent>;

    /**
     * The dragover event is fired when an element or text selection is being dragged over a valid drop target .
     */
    @Event()
    public dragOverEvent!: EventHandler<DragAndDropEvent>;

    /**
     * The drop event is fired when an element or text selection is dropped on a valid drop target (=contains 'cjsDropContainer' attribute)
     */
    @Event()
    public dropEvent!: EventHandler<DragAndDropEvent>;

    /**
     * The dragend event is fired when a drag operation is being ended
     */
    @Event()
    public dragEndEvent!: EventHandler<DragAndDropEvent>;


    /**
     * The dragAbort event is fired when a drag operation hasn't started and mouseup was triggered
     * Drag is registered when user mousedown on element. If user never move his mouse and release mouse button (dragAndDrop hasn't started), an abort event is emitted.
     */
    @Event()
    public dragAbortEvent!: EventHandler<DragAndDropAbortEvent>;

    /**
     * Turns an element into a draggable item.
     * @param element Element to which to attach the dragging functionality.
     * @param data Object used transfer data during a dragAndDrop sequence
     * @param config Object used to configure the dragging behavior.
     */
    public createDrag<ItemData = anyReallyAny>(
        element: HTMLElement,
        data: ItemData,
        config?: DragRefConfig
    ): DragRef<ItemData> {

        // css class is not used to determine item is draggable but attribute IS
        setHtmlElementOptions(element, {
            attributes: {
                [CJS_DRAGGABLE_ITEM_ATTR]: ""
            },
            cssClass: ["cjs-draggable-item"]
        });

        return new DragRef<ItemData>(this, element, data, config ?? {});
    }

    /**
     * Turns an element into a drop zone where items can be dropped.
     * If not set: "dropEvent" will not be emit
     */
    public configureDropContainer(...elements: HTMLElement[]): void {

        for (const elt of wrapAsArray(elements)) {
            setHtmlElementOptions(elt, {
                attributes: {
                    [CJS_DROP_CONTAINER_ATTR]: ""
                },
                cssClass: ["cjs-drop-container"]
            });
        }
    }
}